/**
 * TemplateManagementPage — System Admin template upload and versioning.
 * Blueprint: F-07 — new UI component, System Admin access only.
 *
 * Per-tort: upload DOCX/PDF template, define merge_field_map, set as active.
 * One active template per tort at a time (is_active=true constraint).
 *
 * TODO: GET /admin/templates/:tort_code — list all versions.
 * TODO: POST /admin/templates/:tort_code — upload new template version.
 * TODO: PUT /admin/templates/:tort_code/activate/:version — set active.
 * TODO: Merge field map editor — visual mapping of placeholder → claimant field.
 * TODO: Preview endpoint — show merged output with sample claimant data.
 */
import React, { useState } from 'react';
import { BaseLayout } from '@/components/layout/BaseLayout';
import { Upload, CheckCircle } from 'lucide-react';
import type { TortCode, ClaimFormTemplate } from '@/types/cmm/claimant.types';

const BREADCRUMBS = [{ label: 'Admin' }, { label: 'Template Management' }];

const TemplateManagementPage: React.FC = () => {
  const [activeTort, setActiveTort] = useState<TortCode>('RLP');
  const [templates,  setTemplates]  = useState<ClaimFormTemplate[]>([]);

  return (
    <BaseLayout title="Template Management" subtitle="Manage claim form templates per tort"
      breadcrumbs={BREADCRUMBS}>
      <div className="space-y-6">
        <div className="flex gap-3">
          {(['SMA','VGA','RLP','PFAS'] as TortCode[]).map(t => (
            <button key={t} onClick={() => setActiveTort(t)}
              className={`px-4 py-2 text-sm font-medium rounded-md ${
                activeTort === t ? 'bg-blue-600 text-white' : 'border border-gray-300 text-gray-700'
              }`}>{t}</button>
          ))}
        </div>

        {/* Upload new template */}
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Upload New Template — {activeTort}</h3>
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
            <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
            <p className="text-sm text-gray-500">Click to upload DOCX or fillable PDF</p>
            <p className="text-xs text-gray-400 mt-1">Supports: DOCX, PDF_FILLABLE, PDF_STATIC</p>
          </div>
          {/* TODO: merge_field_map visual editor after file selected */}
        </div>

        {/* Version history */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-900">Version History — {activeTort}</h3>
          </div>
          {templates.length === 0 ? (
            <div className="py-12 text-center text-sm text-gray-400">
              No templates uploaded yet for {activeTort}.
              {/* TODO: Populate from GET /admin/templates/:tort_code */}
            </div>
          ) : templates.map(t => (
            <div key={t.id} className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
              <div>
                <p className="text-sm font-medium text-gray-800">v{t.version} — {t.templateName}</p>
                <p className="text-xs text-gray-400">{t.templateFormat} · {new Date(t.createdAt).toLocaleDateString()}</p>
              </div>
              {t.isActive
                ? <span className="flex items-center gap-1 text-xs text-green-600"><CheckCircle className="w-3.5 h-3.5" /> Active</span>
                : <button className="text-xs text-blue-600 hover:underline">Set Active</button>
              }
            </div>
          ))}
        </div>
      </div>
    </BaseLayout>
  );
};

export default TemplateManagementPage;
